// ── Export-Auswahl (Asset oder Jahresbericht) ──────────────────────
// Nutzt das markeneigene BrandModal + ChoiceTile (PDF / Excel). Jahr-Auswahl
// als Segmented über den Kacheln. Props-API unverändert (open/scope/asset/
// availableYears/isPremium/onPick/onLocked) — alle Aufrufer bleiben kompatibel.

import { useState, useMemo } from 'react';
import { F } from '../../styles/theme';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import BrandModal, { ChoiceGrid, ChoiceTile } from './BrandModal';
import { Segmented } from './Editorial';
import TierBadge from './TierBadge';

// PDF — Dokument mit umgeknickter Ecke + Textzeilen
const PDF_GLYPH = (
  <>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </>
);

// Excel — Tabellen-Raster
const XLSX_GLYPH = (
  <>
    <rect x="4" y="4" width="16" height="16" rx="1.5" />
    <path d="M4 10h16" />
    <path d="M4 15h16" />
    <path d="M10 4v16" />
  </>
);

function YearSegmented({ T, years, value, onChange }) {
  const { t } = useT();
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontFamily: F.family, fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em',
        color: T.sub, marginBottom: 8,
      }}>
        {t('mod_export_report_year')}
      </div>
      <Segmented
        value={value}
        onChange={onChange}
        options={years.map(y => ({ value: y, label: String(y) }))}
      />
    </div>
  );
}

export default function ExportSheet({
  open,
  onClose,
  scope,            // "asset" | "year"
  asset,            // bei scope=asset
  availableYears,   // bei scope=year — Array of number
  isPremium,
  onPick,           // (format: "pdf" | "xlsx", year?: number) => void
  onLocked,         // statt onPick wenn Pick Premium braucht aber nicht aktiv
}) {
  const { T } = useTheme();
  const { t } = useT();
  const years = useMemo(() => availableYears && availableYears.length > 0
    ? availableYears
    : [new Date().getFullYear()], [availableYears]);
  const [year, setYear] = useState(years[0]);

  const title = scope === 'asset'
    ? t('mod_export_asset_title', { name: asset?.name || asset?.sym || 'Asset' })
    : t('mod_export_report_title');
  const subtitle = scope === 'asset'
    ? t('mod_export_sub_asset')
    : t('mod_export_sub_year');

  const locked = !isPremium;

  const handlePick = (format) => {
    if (locked) { onLocked && onLocked(); return; }
    onPick(format, scope === 'year' ? year : undefined);
  };

  const footer = !isPremium
    ? (scope === 'year'
        ? <>{t('mod_export_unlock_pre')} <span style={{ color: T.red, fontWeight: 700 }}>{t('mod_export_unlock_report')}</span> €19 · <span style={{ color: T.red, fontWeight: 700 }}>{t('premium')}</span> €49{t('mod_per_year_suffix')}</>
        : <>{t('mod_export_unlock_asset_pre')} <span style={{ color: T.red, fontWeight: 700 }}>{t('premium')}</span> €49{t('mod_per_year_suffix')}</>)
    : t('mod_export_footer_ready');

  return (
    <BrandModal
      open={open}
      onClose={onClose}
      eyebrow={scope === 'asset' ? t('mod_export_eyebrow_asset') : t('mod_export_eyebrow_report')}
      title={title}
      subtitle={subtitle}
      footer={footer}
    >
      {scope === 'year' && (
        <YearSegmented T={T} years={years} value={year} onChange={setYear} />
      )}

      <ChoiceGrid>
        <ChoiceTile
          glyph={PDF_GLYPH}
          label="PDF"
          sub={t('mod_export_pdf_sub')}
          badge={locked ? <TierBadge tier="premium" scale={0.8} /> : null}
          onClick={() => handlePick('pdf')}
        />
        <ChoiceTile
          glyph={XLSX_GLYPH}
          label="Excel"
          sub={t('mod_export_xlsx_sub')}
          badge={locked ? <TierBadge tier="premium" scale={0.8} /> : null}
          onClick={() => handlePick('xlsx')}
        />
      </ChoiceGrid>
    </BrandModal>
  );
}
